import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-md border transition-colors focus-ring smoothing-default',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-accent text-accent-foreground shadow hover:bg-accent-hover data-[selected=true]:bg-accent-active',
        secondary:
          'border border-border bg-secondary text-foreground hover:bg-secondary-hover data-[selected=true]:bg-secondary-active',
        destructive:
          'border-transparent bg-danger-9 text-white shadow hover:bg-danger-10 data-[selected=true]:bg-danger-11',
        'destructive-subtle':
          'border-transparent bg-danger-4 text-danger-12 shadow hover:bg-danger-5 data-[selected=true]:bg-danger-6',
        outline:
          'text-foreground border-border border-accent text-accent data-[selected=true]:bg-secondary-active',
        warning:
          'border-transparent bg-warning-9 text-warning-12 shadow hover:bg-warning-10 data-[selected=true]:bg-warning-11 data-[selected=true]:text-warning-1 dark:text-warning-1',
        'warning-subtle':
          'border-transparent bg-warning-3 text-warning-11 hover:bg-warning-4 data-[selected=true]:bg-warning-5',
        success:
          'border-transparent bg-success-9 text-success-1 shadow hover:bg-success-10 data-[selected=true]:bg-success-11',
        'success-subtle':
          'border-transparent bg-success-3 text-success-11 hover:bg-success-4 data-[selected=true]:bg-success-5',
        'dot-destructive':
          'size-3 p-0 rounded-full border-transparent bg-danger-9 text-danger-1 shadow hover:bg-danger-10 data-[selected=true]:bg-danger-11',
      },
      size: {
        xs: 'text-xs px-1.5 py-[0.1rem]',
        sm: 'text-sm px-2 py-[0.2rem]',
        md: 'text-base px-2.5 py-0.5 ',
        lg: 'text-lg px-3 py-1',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'xs',
    },
  }
);

export interface BadgeProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: BadgeVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
}

function Badge({ className, size = 'xs', variant, ...props }: BadgeProps) {
  return (
    <div
      className={cn(badgeVariants({ variant, size }), className)}
      {...props}
    />
  );
}

type BadgeVariant = VariantProps<typeof badgeVariants>['variant'];

export { Badge, badgeVariants, type BadgeVariant };
